import React, { useState, useEffect } from 'react';
import carrierService, { Carrier, CreateCarrierRequest, UpdateCarrierRequest } from '../services/carrierService';
import './CarrierManagement.css';

interface CarrierManagementProps {
  onClose?: () => void;
}

const CarrierManagement: React.FC<CarrierManagementProps> = ({ onClose }) => {
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingCarrier, setEditingCarrier] = useState<Carrier | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);

  // Состояния для ошибок валидации
  const [createFormErrors, setCreateFormErrors] = useState<{[key: string]: string}>({});
  const [editFormErrors, setEditFormErrors] = useState<{[key: string]: string}>({});

  // Функции валидации
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^[+]?[0-9\s\-()]+$/;
    return phoneRegex.test(phone);
  };

  const validateWebsite = (website: string): boolean => {
    const websiteRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})[\/\w .-]*\/?$/;
    return websiteRegex.test(website);
  };

  const validateCreateForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    // Проверка обязательных полей
    if (!createForm.code.trim()) {
      errors.code = 'Код обязателен';
    }
    if (!createForm.name.trim()) {
      errors.name = 'Название обязательно';
    }
    if (!createForm.displayName.trim()) {
      errors.displayName = 'Отображаемое название обязательно';
    }

    // Проверка email
    if (createForm.email && !validateEmail(createForm.email)) {
      errors.email = 'Некорректный формат email';
    }

    // Проверка телефона
    if (createForm.phone && !validatePhone(createForm.phone)) {
      errors.phone = 'Некорректный формат телефона';
    }

    // Проверка веб-сайта
    if (createForm.website && !validateWebsite(createForm.website)) {
      errors.website = 'Некорректный формат веб-сайта';
    }

    setCreateFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateEditForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    // Проверка email
    if (editForm.email && !validateEmail(editForm.email)) {
      errors.email = 'Некорректный формат email';
    }

    // Проверка телефона
    if (editForm.phone && !validatePhone(editForm.phone)) {
      errors.phone = 'Некорректный формат телефона';
    }

    // Проверка веб-сайта
    if (editForm.website && !validateWebsite(editForm.website)) {
      errors.website = 'Некорректный формат веб-сайта';
    }

    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Формы
  const [createForm, setCreateForm] = useState<CreateCarrierRequest>({
    code: '',
    name: '',
    displayName: '',
    description: '',
    country: '',
    website: '',
    phone: '',
    email: ''
  });

  const [editForm, setEditForm] = useState<UpdateCarrierRequest>({
    code: '',
    name: '',
    displayName: '',
    description: '',
    country: '',
    website: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    fetchCarriers();
  }, []);

  const fetchCarriers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await carrierService.getCarriers();
      setCarriers(response.content);
    } catch (err) {
      console.error('Error fetching carriers:', err);
      setError('Ошибка при загрузке перевозчиков');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCarrier = async () => {
    try {
      // Очищаем предыдущие ошибки
      setCreateFormErrors({});

      // Валидируем форму
      if (!validateCreateForm()) {
        return;
      }

      await carrierService.createCarrier(createForm);
      setIsCreateModalOpen(false);
      setCreateForm({
        code: '',
        name: '',
        displayName: '',
        description: '',
        country: '',
        website: '',
        phone: '',
        email: ''
      });
      fetchCarriers();
      alert('Перевозчик успешно создан!');
    } catch (err: any) {
      console.error('Error creating carrier:', err);
      
      // Обрабатываем ошибки валидации с сервера
      if (err.response?.data?.message) {
        const serverError = err.response.data.message;
        if (serverError.includes('phone')) {
          setCreateFormErrors(prev => ({ ...prev, phone: 'Некорректный формат телефона' }));
        } else if (serverError.includes('email')) {
          setCreateFormErrors(prev => ({ ...prev, email: 'Некорректный формат email' }));
        } else if (serverError.includes('website')) {
          setCreateFormErrors(prev => ({ ...prev, website: 'Некорректный формат веб-сайта' }));
        } else {
          alert(`Ошибка при создании перевозчика: ${serverError}`);
        }
      } else {
        alert('Ошибка при создании перевозчика');
      }
    }
  };

  const handleUpdateCarrier = async () => {
    try {
      if (!editingCarrier) return;

      // Очищаем предыдущие ошибки
      setEditFormErrors({});

      // Валидируем форму
      if (!validateEditForm()) {
        return;
      }

      const updateData: UpdateCarrierRequest = {};
      if (editForm.code) updateData.code = editForm.code;
      if (editForm.name) updateData.name = editForm.name;
      if (editForm.displayName) updateData.displayName = editForm.displayName;
      if (editForm.description !== undefined) updateData.description = editForm.description;
      if (editForm.country !== undefined) updateData.country = editForm.country;
      if (editForm.website !== undefined) updateData.website = editForm.website;
      if (editForm.phone !== undefined) updateData.phone = editForm.phone;
      if (editForm.email !== undefined) updateData.email = editForm.email;

      await carrierService.updateCarrier(editingCarrier.id, updateData);
      setEditingCarrier(null);
      setEditForm({
        code: '',
        name: '',
        displayName: '',
        description: '',
        country: '',
        website: '',
        phone: '',
        email: ''
      });
      fetchCarriers();
      alert('Перевозчик успешно обновлен!');
    } catch (err: any) {
      console.error('Error updating carrier:', err);
      
      // Обрабатываем ошибки валидации с сервера
      if (err.response?.data?.message) {
        const serverError = err.response.data.message;
        if (serverError.includes('phone')) {
          setEditFormErrors(prev => ({ ...prev, phone: 'Некорректный формат телефона' }));
        } else if (serverError.includes('email')) {
          setEditFormErrors(prev => ({ ...prev, email: 'Некорректный формат email' }));
        } else if (serverError.includes('website')) {
          setEditFormErrors(prev => ({ ...prev, website: 'Некорректный формат веб-сайта' }));
        } else {
          alert(`Ошибка при обновлении перевозчика: ${serverError}`);
        }
      } else {
        alert('Ошибка при обновлении перевозчика');
      }
    }
  };

  const handleDeleteCarrier = async (carrier: Carrier) => {
    if (!window.confirm(`Вы уверены, что хотите удалить перевозчика "${carrier.displayName}"?`)) {
      return;
    }

    try {
      await carrierService.deleteCarrier(carrier.id);
      fetchCarriers();
      alert('Перевозчик успешно удален!');
    } catch (err) {
      console.error('Error deleting carrier:', err);
      alert('Ошибка при удалении перевозчика');
    }
  };

  const handleCreateDefaultCarriers = async () => {
    try {
      const defaultCarriers = carrierService.getDefaultCarriers();

      for (const carrierData of defaultCarriers) {
        await carrierService.createCarrier(carrierData);
      }

      fetchCarriers();
      alert('Стандартные перевозчики успешно созданы!');
    } catch (err) {
      console.error('Error creating default carriers:', err);
      alert('Ошибка при создании стандартных перевозчиков');
    }
  };

  const filteredCarriers = carriers.filter(carrier => {
    const matchesSearch = 
      carrier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      carrier.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      carrier.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (carrier.description && carrier.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (carrier.country && carrier.country.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesActive = !showActiveOnly || carrier.isActive;
    return matchesSearch && matchesActive;
  });

  const openEditModal = (carrier: Carrier) => {
    setEditingCarrier(carrier);
    setEditForm({
      code: carrier.code,
      name: carrier.name,
      displayName: carrier.displayName,
      description: carrier.description || '',
      country: carrier.country || '',
      website: carrier.website || '',
      phone: carrier.phone || '',
      email: carrier.email || ''
    });
  };

  const closeEditModal = () => {
    setEditingCarrier(null);
    setEditForm({
      code: '',
      name: '',
      displayName: '',
      description: '',
      country: '',
      website: '',
      phone: '',
      email: ''
    });
    setEditFormErrors({});
  };

  if (loading) {
    return (
      <div className="carrier-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка перевозчиков...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="carrier-management">
      <div className="carrier-header">
        <h2>Управление перевозчиками</h2>
        <div className="carrier-actions">
          <button 
            onClick={() => setIsCreateModalOpen(true)} 
            className="create-carrier-btn"
          >
            Создать перевозчика
          </button>
          <button 
            onClick={handleCreateDefaultCarriers} 
            className="create-default-btn"
          >
            Стандартные перевозчики
          </button>
        </div>
      </div>

      {error && (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchCarriers} className="retry-button">
            Попробовать снова
          </button>
        </div>
      )}

      <div className="carrier-filters">
        <input
          type="text"
          placeholder="Поиск по названию, коду, описанию, стране..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <label className="filter-checkbox">
          <input
            type="checkbox"
            checked={showActiveOnly}
            onChange={(e) => setShowActiveOnly(e.target.checked)}
          />
          Только активные
        </label>
      </div>

      <div className="carriers-list">
        {filteredCarriers.map((carrier) => (
          <div key={carrier.id} className={`carrier-card ${!carrier.isActive ? 'inactive' : ''}`}>
            <div className="carrier-info">
              <div className="carrier-header-info">
                <div className="carrier-code">{carrier.code}</div>
                <h3 className="carrier-name">{carrier.displayName}</h3>
                <span className={`carrier-active-badge ${carrier.isActive ? 'active' : 'inactive'}`}>
                  {carrier.isActive ? 'Активен' : 'Неактивен'}
                </span>
              </div>
              {carrier.description && (
                <p className="carrier-description">{carrier.description}</p>
              )}
              <div className="carrier-details">
                {carrier.country && <span className="carrier-country">Страна: {carrier.country}</span>}
                {carrier.website && <span className="carrier-website">Сайт: {carrier.website}</span>}
                {carrier.phone && <span className="carrier-phone">Тел: {carrier.phone}</span>}
                {carrier.email && <span className="carrier-email">Email: {carrier.email}</span>}
              </div>
              <div className="carrier-meta">
                <span>Создан: {new Date(carrier.createdAt).toLocaleDateString('ru-RU')}</span>
                <span>Обновлен: {new Date(carrier.updatedAt).toLocaleDateString('ru-RU')}</span>
              </div>
            </div>
            <div className="carrier-actions">
              <button 
                onClick={() => openEditModal(carrier)} 
                className="edit-btn"
              >
                Редактировать
              </button>
              <button 
                onClick={() => handleDeleteCarrier(carrier)} 
                className="delete-btn"
              >
                Удалить
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredCarriers.length === 0 && !loading && (
        <div className="empty-state">
          <p>Перевозчики не найдены</p>
        </div>
      )}

      {/* Модальное окно создания */}
      {isCreateModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Создать нового перевозчика</h3>
              <button onClick={() => setIsCreateModalOpen(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Код *</label>
                  <input
                    type="text"
                    value={createForm.code}
                    onChange={(e) => setCreateForm({ ...createForm, code: e.target.value })}
                    placeholder="KC"
                  />
                  {createFormErrors.code && <p className="error-message">{createFormErrors.code}</p>}
                </div>
                <div className="form-group">
                  <label>Название *</label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder="Air Astana"
                  />
                  {createFormErrors.name && <p className="error-message">{createFormErrors.name}</p>}
                </div>
              </div>
              <div className="form-group">
                <label>Отображаемое название *</label>
                <input
                  type="text"
                  value={createForm.displayName}
                  onChange={(e) => setCreateForm({ ...createForm, displayName: e.target.value })}
                  placeholder="Air Astana"
                />
                {createFormErrors.displayName && <p className="error-message">{createFormErrors.displayName}</p>}
              </div>
              <div className="form-group">
                <label>Описание</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  placeholder="Описание перевозчика"
                  rows={3}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Страна</label>
                  <input
                    type="text"
                    value={createForm.country}
                    onChange={(e) => setCreateForm({ ...createForm, country: e.target.value })}
                    placeholder="Казахстан"
                  />
                </div>
                <div className="form-group">
                  <label>Веб-сайт</label>
                  <input
                    type="url"
                    value={createForm.website}
                    onChange={(e) => setCreateForm({ ...createForm, website: e.target.value })}
                    placeholder="https://www.airastana.com"
                  />
                  <small className="form-hint">Формат: https://www.example.com</small>
                  {createFormErrors.website && <p className="error-message">{createFormErrors.website}</p>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Телефон</label>
                  <input
                    type="tel"
                    value={createForm.phone}
                    onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                    placeholder="+7 727 244 44 44"
                  />
                  <small className="form-hint">Формат: +7 727 244 44 44</small>
                  {createFormErrors.phone && <p className="error-message">{createFormErrors.phone}</p>}
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    placeholder="info@airastana.com"
                  />
                  <small className="form-hint">Формат: user@example.com</small>
                  {createFormErrors.email && <p className="error-message">{createFormErrors.email}</p>}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => {
                setIsCreateModalOpen(false);
                setCreateFormErrors({});
              }} className="cancel-btn">
                Отмена
              </button>
              <button onClick={handleCreateCarrier} className="save-btn">
                Создать
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно редактирования */}
      {editingCarrier && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Редактировать перевозчика</h3>
              <button onClick={closeEditModal} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Код</label>
                  <input
                    type="text"
                    value={editForm.code}
                    onChange={(e) => setEditForm({ ...editForm, code: e.target.value })}
                    placeholder="KC"
                  />
                </div>
                <div className="form-group">
                  <label>Название</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    placeholder="Air Astana"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Отображаемое название</label>
                <input
                  type="text"
                  value={editForm.displayName}
                  onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                  placeholder="Air Astana"
                />
              </div>
              <div className="form-group">
                <label>Описание</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder="Описание перевозчика"
                  rows={3}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Страна</label>
                  <input
                    type="text"
                    value={editForm.country}
                    onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                    placeholder="Казахстан"
                  />
                </div>
                <div className="form-group">
                  <label>Веб-сайт</label>
                  <input
                    type="url"
                    value={editForm.website}
                    onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                    placeholder="https://www.airastana.com"
                  />
                  <small className="form-hint">Формат: https://www.example.com</small>
                  {editFormErrors.website && <p className="error-message">{editFormErrors.website}</p>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Телефон</label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    placeholder="+7 727 244 44 44"
                  />
                  <small className="form-hint">Формат: +7 727 244 44 44</small>
                  {editFormErrors.phone && <p className="error-message">{editFormErrors.phone}</p>}
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    placeholder="info@airastana.com"
                  />
                  <small className="form-hint">Формат: user@example.com</small>
                  {editFormErrors.email && <p className="error-message">{editFormErrors.email}</p>}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={closeEditModal} className="cancel-btn">
                Отмена
              </button>
              <button onClick={handleUpdateCarrier} className="save-btn">
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarrierManagement; 